if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends BaseElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      connectedCallback() {
        super.connectedCallback();

        const input = this.input;
        if (!input) return;

        const hide = this.hide.bind(this);
        ['pointerdown', 'input'].forEach((event) => this.on(input, event, hide));
      }

      get input() {
        return this.closest('form')?.querySelector('input[type="search"]');
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      hide() {
        if (this.parentElement.hasAttribute('hidden')) return;
        this.parentElement.setAttribute('hidden', '');
      }

      async init() {
        if (this.initialized) return;
        this.initialized = true;

        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.textContent = text;
        if (!this.scrollWidth) {
          this.style.removeProperty('width');
          this.cursor.classList.add('blink');
          return;
        }

        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        if (!this.scrollWidth) return;

        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.textContent = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends BaseElementMixin(HTMLFormElement) {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.cacheTtl = 5 * 60 * 1000;
        this.stockFallbackFailures = new Map();
        this.stockFallbackFailureTtl = 30 * 1000;
        this.stockFallbackTimeout = 2500;
        this.requestSequence = 0;
        this.lifecycleSequence = 0;
        this.activeOptionIndex = -1;
      }

      connectedCallback() {
        super.connectedCallback();

        const lifecycle = ++this.lifecycleSequence;
        this.focusElement = this.input;
        this.on(this.resetButton, 'click', this.clear.bind(this));
        const onInput = theme.utils.debounce(requestId => {
          if (lifecycle === this.lifecycleSequence && requestId === this.requestSequence) this.onChange();
        }, 220);
        this.on(this.input, 'input', () => {
          this.invalidatePendingRequest();
          this.pendingInputRequest = this.requestSequence;
          onInput(this.pendingInputRequest);
        });
        this.on(this.input, 'focus', this.onFocus.bind(this));
        this.on(this.input, 'keydown', this.onKeydown.bind(this));
        this.on(this.input, 'keyup', event => {
          if (event.key === 'Escape' && this.consumedEscape) {
            event.stopPropagation();
            this.consumedEscape = false;
          }
        });
        this.on(this, 'focusout', this.onFocusOut.bind(this));
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.lifecycleSequence += 1;
        this.requestSequence += 1;
        this.renderAbortController?.abort();
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get dropdownParent() {
        return this.closest('.collection') || this.closest('.search-inline');
      }

      get resultsTarget() {
        return document.getElementById('PredictiveSearchResults-' + theme.utils.sectionId(this));
      }

      get listbox() {
        return document.getElementById('PredictiveSearchResults-' + theme.utils.sectionId(this) + '-Listbox');
      }

      get statusElement() {
        return document.getElementById('PredictiveSearchStatus-' + theme.utils.sectionId(this));
      }

      get limit() {
        const limit = this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3;
        return Math.min(10, Math.max(1, Number.isFinite(limit) ? limit : 3));
      }

      onFocus() {
        this.open();
        if (this.getQuery().length === 0) return;
        this.performSearch(this.getQuery());
      }

      onKeydown(event) {
        const options = this.hasAttribute('results') ? this.getOptions() : [];

        if (event.key === 'ArrowDown' && options.length) {
          event.preventDefault();
          this.setActiveOption((this.activeOptionIndex + 1) % options.length);
          return;
        }

        if (event.key === 'ArrowUp' && options.length) {
          event.preventDefault();
          const nextIndex = this.activeOptionIndex <= 0 ? options.length - 1 : this.activeOptionIndex - 1;
          this.setActiveOption(nextIndex);
          return;
        }

        if (event.key === 'Enter' && this.activeOptionIndex >= 0 && options[this.activeOptionIndex]) {
          event.preventDefault();
          options[this.activeOptionIndex].click();
          return;
        }

        if (event.key === 'Escape' && (
          this.hasAttribute('results') || this.hasAttribute('loading') || this.pendingInputRequest === this.requestSequence
        )) {
          event.preventDefault();
          event.stopPropagation();
          this.consumedEscape = true;
          this.close();
          this.input.focus();
        }
      }

      onFocusOut(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.close();
      }

      invalidatePendingRequest() {
        this.requestSequence += 1;
        this.renderAbortController?.abort();
        this.removeAttribute('loading');
        this.removeAttribute('results');
        this.resultsTarget?.setAttribute('aria-busy', 'false');
        this.setExpanded(false);
        this.clearActiveOption();
        this.announce();
      }

      open() {
        if (this.dropdownParent) document.body.classList.add('predictive-search-open');
      }

      close() {
        this.requestSequence += 1;
        this.renderAbortController?.abort();
        document.body.classList.remove('predictive-search-open');
        this.removeAttribute('loading');
        this.removeAttribute('results');
        this.resultsTarget?.setAttribute('aria-busy', 'false');
        this.setExpanded(false);
        this.clearActiveOption();
        this.announce();
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.requestSequence += 1;
        this.renderAbortController?.abort();
        this.input.value = '';
        this.input.focus();
        this.removeAttribute('loading');
        this.removeAttribute('results');
        this.resultsTarget?.setAttribute('aria-busy', 'false');
        this.setExpanded(false);
        this.clearActiveOption();
        this.announce();
      }

      getQuery() {
        const query = theme.search?.cleanQuery(this.input.value) || this.input.value.trim().replace(/\s+/g, ' ');
        return query.slice(0, 128);
      }

      buildUrl(query = this.getQuery(), { enhanced = true, productOnly = false } = {}) {
        const url = new URL(theme.routes.predictive_search_url, theme.routes.shop_url);

        url.searchParams.set('q', query);
        url.searchParams.set('resources[limit]', this.limit);
        url.searchParams.set('resources[limit_scope]', 'each');
        if (productOnly) url.searchParams.set('resources[type]', 'product');
        if (enhanced) {
          url.searchParams.set(
            'resources[options][fields]',
            'title,product_type,vendor,tag,variants.title,variants.sku,variants.barcode'
          );
          // Keep unavailable-product visibility inherited from Search & Discovery.
        }
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      buildStockFallbackUrl(query) {
        const url = new URL(theme.routes.search_url, theme.routes.shop_url);

        url.searchParams.set('q', query);
        url.searchParams.set('options[prefix]', 'last');
        url.searchParams.set('sort_by', 'relevance');
        url.searchParams.set('section_id', 'search-stock-fallback');
        return url;
      }

      onChange() {
        const query = this.getQuery();
        this.clearActiveOption();

        if (query.length === 0) {
          this.clear();
          return;
        }

        this.open();
        this.performSearch(query);
      }

      renderUnsupportedHint() {
        if (!theme.strings.searchUnsupportedLocale?.length) {
          this.close();
          this.removeAttribute('results');
          return;
        }

        const target = this.resultsTarget;
        if (target) {
          const listbox = document.createElement('div');
          listbox.id = 'PredictiveSearchResults-' + theme.utils.sectionId(this) + '-Listbox';
          listbox.className = 'search__listbox grid gap-10 w-full';
          listbox.setAttribute('role', 'listbox');

          const hint = document.createElement('p');
          hint.className = 'predictive-search__hint text-sm leading-tight';
          hint.textContent = theme.strings.searchUnsupportedLocale;
          target.replaceChildren(listbox, hint);
        }

        this.setExpanded(false);
        this.setAttribute('results', '');
      }

      async performSearch(query) {
        if (!theme.config.predictiveSearch) {
          this.renderUnsupportedHint();
          return;
        }

        const requestId = ++this.requestSequence;
        this.renderAbortController?.abort();
        this.renderAbortController = new AbortController();
        const { signal } = this.renderAbortController;

        this.setAttribute('loading', '');
        this.resultsTarget?.setAttribute('aria-busy', 'true');

        try {
          let primaryText;
          let compatibilityRetried = false;
          try {
            primaryText = await this.fetchResponse(this.buildUrl(query).toString(), signal);
          } catch (error) {
            if (requestId !== this.requestSequence || signal.aborted) return;
            if (error.status === 417) {
              theme.config.predictiveSearch = false;
              this.renderUnsupportedHint();
              return;
            }
            if (error.status !== 422) throw error;
            compatibilityRetried = true;
            primaryText = await this.fetchResponse(this.buildUrl(query, { enhanced: false }).toString(), signal);
          }

          if (requestId !== this.requestSequence || signal.aborted) return;
          const primary = this.parseResults(primaryText);
          const primaryProducts = this.extractProducts(primary, 0);
          const stockFallbackQuery = theme.search?.stockFallbackQuery(query) || '';
          const needsStockFallback = !compatibilityRetried
            && Boolean(stockFallbackQuery)
            && theme.search?.needsStockFallback(
              primaryProducts,
              query,
              { nativeEmpty: Boolean(primary?.querySelector('.search__empty')) }
            );

          if (needsStockFallback) {
            const stockFallbackUrl = this.buildStockFallbackUrl(stockFallbackQuery).toString();
            const failureKey = `${theme.search.fold(query)}:${stockFallbackUrl}`;
            const failureExpiresAt = this.stockFallbackFailures.get(failureKey) || 0;
            if (failureExpiresAt > Date.now()) {
              this.renderSearchResults(this.mergeSearchResults(primaryText, null, query));
              return;
            }
            this.stockFallbackFailures.delete(failureKey);

            try {
              const stockFallbackText = await this.fetchResponse(
                stockFallbackUrl,
                signal,
                `full-search-fallback:${stockFallbackUrl}`,
                this.stockFallbackTimeout
              );
              if (requestId !== this.requestSequence || signal.aborted) return;

              this.renderSearchResults(
                this.mergeSearchResults(primaryText, stockFallbackText, query, 'full-search-fallback')
              );
            } catch (error) {
              if (error.name !== 'AbortError' && !error.status) console.error(error);
              if (requestId === this.requestSequence && !signal.aborted) {
                this.stockFallbackFailures.set(failureKey, Date.now() + this.stockFallbackFailureTtl);
                if (this.stockFallbackFailures.size > 30) {
                  this.stockFallbackFailures.delete(this.stockFallbackFailures.keys().next().value);
                }
                this.renderSearchResults(this.mergeSearchResults(primaryText, null, query));
              }
            }
            return;
          }

          this.renderSearchResults(this.mergeSearchResults(primaryText, null, query));
          if (compatibilityRetried) return;

          const alternatives = theme.search?.candidates(query) || [query];
          const alternative = alternatives.slice(1)[0];

          if (!alternative || !theme.search?.needsFallback(primaryProducts, query)) return;

          try {
            const fallbackText = await this.fetchResponse(
              this.buildUrl(alternative, { enhanced: true, productOnly: true }).toString(),
              signal
            );
            if (requestId !== this.requestSequence || signal.aborted) return;

            const mergedText = this.mergeSearchResults(primaryText, fallbackText, query, 'predictive-fallback');
            this.renderSearchResults(mergedText);
          } catch (error) {
            if (error.name !== 'AbortError' && !error.status) console.error(error);
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            if (!error.status) console.error(error);
            if (requestId === this.requestSequence) {
              this.close();
              this.announceError();
            }
          }
        } finally {
          if (requestId === this.requestSequence) {
            this.removeAttribute('loading');
            this.resultsTarget?.setAttribute('aria-busy', 'false');
          }
        }
      }

      fetchResponse(url, signal, cacheKey = url, timeout = 0) {
        const now = Date.now();
        const cached = this.cachedMap.get(cacheKey);
        if (cached && cached.expiresAt > now) {
          this.cachedMap.delete(cacheKey);
          this.cachedMap.set(cacheKey, cached);
          return Promise.resolve(cached.text);
        }
        if (cached) this.cachedMap.delete(cacheKey);

        const timeoutController = timeout > 0 ? new AbortController() : null;
        const abortTimedRequest = () => timeoutController?.abort();
        const requestSignal = timeoutController?.signal || signal;
        const timeoutId = timeoutController ? setTimeout(abortTimedRequest, timeout) : null;
        if (timeoutController) {
          if (signal.aborted) abortTimedRequest();
          else signal.addEventListener('abort', abortTimedRequest, { once: true });
        }

        return fetch(url, { signal: requestSignal }).then((response) => {
          if (!response.ok) {
            const error = new Error(`Predictive search ${response.status}`);
            error.status = response.status;
            throw error;
          }
          return response.text();
        }).then((text) => {
          this.cachedMap.set(cacheKey, { text, expiresAt: Date.now() + this.cacheTtl });
          if (this.cachedMap.size > 30) {
            this.cachedMap.delete(this.cachedMap.keys().next().value);
          }
          return text;
        }).finally(() => {
          if (timeoutId != null) clearTimeout(timeoutId);
          if (timeoutController) signal.removeEventListener('abort', abortTimedRequest);
        });
      }

      parseResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        return new DOMParser().parseFromString(responseText, 'text/html').getElementById(id);
      }

      parseStockFallbackResults(responseText, expectedQuery = '') {
        const container = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('[data-search-stock-fallback]');
        if (!container || !expectedQuery) return container;

        return theme.search.cleanQuery(container.dataset.searchTerms) === theme.search.cleanQuery(expectedQuery)
          ? container
          : null;
      }

      extractProducts(responseText, sourcePriority = 0, source = 'predictive-primary') {
        const container = typeof responseText === 'string' ? this.parseResults(responseText) : responseText;
        if (!container) return [];

        return Array.from(container.querySelectorAll('[data-search-product-id]')).map((element, index) => ({
          element,
          id: element.dataset.searchProductId,
          title: element.dataset.searchTitle || '',
          productType: element.dataset.searchProductType || '',
          vendor: element.dataset.searchVendor || '',
          url: element.dataset.searchUrl || element.querySelector('a[href]')?.getAttribute('href') || '',
          available: element.dataset.searchAvailable === 'true',
          exactSku: element.dataset.searchExactSku === 'true',
          exactBarcode: element.dataset.searchExactBarcode === 'true',
          categoryMatch: element.dataset.searchCategoryMatch === 'true',
          source,
          nativeRank: sourcePriority * 100 + Number(element.dataset.searchNativeRank || index + 1)
        }));
      }

      mergeSearchResults(primaryText, fallbackText, query, fallbackSource = 'predictive-fallback') {
        const primary = this.parseResults(primaryText);
        const fallback = fallbackText
          ? fallbackSource === 'full-search-fallback'
            ? this.parseStockFallbackResults(fallbackText, theme.search.stockFallbackQuery(query))
            : this.parseResults(fallbackText)
          : null;
        if (!primary || !theme.search) return primaryText;

        const primaryProducts = this.extractProducts(primary, 0, 'predictive-primary');
        const fallbackProducts = fallback ? this.extractProducts(fallback, 1, fallbackSource) : [];
        const productCandidates = [...primaryProducts, ...fallbackProducts];
        const hasFullSearchProducts = fallbackSource === 'full-search-fallback' && fallbackProducts.length > 0;
        const ranker = hasFullSearchProducts
          ? theme.search.rankStockFallbackProducts
          : theme.search.rankProducts;
        const products = ranker(productCandidates, query).slice(0, this.limit);

        const listboxId = 'PredictiveSearchResults-' + theme.utils.sectionId(this) + '-Listbox';
        const primaryListbox = primary.ownerDocument.getElementById(listboxId);
        const fallbackListbox = fallbackSource === 'full-search-fallback'
          ? fallback
          : fallback?.ownerDocument.getElementById(listboxId);
        if (!primaryListbox) return primary.outerHTML;

        let productGrid = primaryListbox.querySelector('.horizontal-products');
        if (!products.length) {
          productGrid?.parentElement?.remove();
          return primary.outerHTML;
        }

        if (!productGrid) {
          const fallbackGroup = fallbackListbox?.querySelector('.horizontal-products')?.parentElement;
          if (!fallbackGroup) return primary.outerHTML;
          const productGroup = fallbackGroup.cloneNode(true);
          primaryListbox.prepend(productGroup);
          productGrid = productGroup.querySelector('.horizontal-products');
        }

        productGrid.replaceChildren(...products.map((product) => {
          const element = product.element.cloneNode(true);
          if (product.source === 'predictive-fallback') this.preserveOriginalQueryInLinks(element, query);
          if (product.source === 'full-search-fallback') {
            this.canonicalizeFullSearchLinks(element);
            element.dataset.searchSource = 'full-search-fallback';
          }
          return element;
        }));
        primary.querySelector('.search__empty')?.remove();

        if (!primary.querySelector('.drawer__sticky')) {
          const fallbackFooter = fallback?.querySelector('.drawer__sticky');
          if (fallbackFooter) primary.appendChild(fallbackFooter.cloneNode(true));
        }

        return primary.outerHTML;
      }

      canonicalizeFullSearchLinks(element) {
        const shopUrl = new URL(theme.routes.shop_url);
        const canonicalPath = (href) => {
          try {
            const url = new URL(href, shopUrl);
            return url.origin === shopUrl.origin ? url.pathname : '';
          } catch (error) {
            return '';
          }
        };

        element.querySelectorAll('a[href]').forEach((link) => {
          const path = canonicalPath(link.getAttribute('href'));
          if (path) link.setAttribute('href', path);
        });

        const productPath = canonicalPath(element.dataset.searchUrl);
        if (productPath) element.dataset.searchUrl = productPath;
      }

      preserveOriginalQueryInLinks(element, query) {
        const shopUrl = new URL(theme.routes.shop_url);

        element.querySelectorAll('a[href]').forEach((link) => {
          const href = link.getAttribute('href');
          try {
            const url = new URL(href, shopUrl);
            if (url.origin !== shopUrl.origin || !url.searchParams.has('_psq')) return;

            url.searchParams.set('_psq', query);
            const absolute = /^[a-z][a-z\d+.-]*:/i.test(href);
            link.setAttribute('href', absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`);
          } catch (error) {
            // Preserve an unexpected native URL unchanged.
          }
        });
      }

      renderSearchResults(responseText) {
        const target = this.resultsTarget;
        if (!target) return;

        const parsed = this.parseResults(responseText);
        if (!parsed) return;

        const activeOption = this.getOptions()[this.activeOptionIndex];
        const activeKey = this.getOptionKey(activeOption);

        target.replaceChildren(...parsed.childNodes);
        this.prepareOptions(activeKey);
        this.setAttribute('results', '');
      }

      prepareOptions(activeKey = '') {
        const listbox = this.listbox;
        if (!listbox) {
          this.setExpanded(false);
          return;
        }

        listbox.querySelectorAll('.horizontal-product__media').forEach((link) => link.setAttribute('tabindex', '-1'));
        const options = this.getOptions();
        options.forEach((option, index) => {
          option.id = `PredictiveSearchOption-${theme.utils.sectionId(this)}-${index}`;
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          option.setAttribute('tabindex', '-1');
        });

        this.activeOptionIndex = -1;
        this.input.removeAttribute('aria-activedescendant');
        if (activeKey) {
          const activeIndex = options.findIndex((option) => this.getOptionKey(option) === activeKey);
          if (activeIndex >= 0) this.setActiveOption(activeIndex);
        }
        this.setExpanded(options.length > 0);
        this.announce(options.length);
      }

      getOptions() {
        if (!this.listbox) return [];
        return Array.from(this.listbox.querySelectorAll('a[href]')).filter(
          (link) => !link.classList.contains('horizontal-product__media')
        );
      }

      getOptionKey(option) {
        if (!option) return '';
        const product = option.closest('[data-search-product-id]');
        return product
          ? `product:${product.dataset.searchProductId}`
          : `url:${option.getAttribute('href') || ''}`;
      }

      setActiveOption(index) {
        const options = this.getOptions();
        if (!options[index]) return;

        options.forEach((option) => {
          option.setAttribute('aria-selected', 'false');
          option.removeAttribute('data-active');
        });
        const option = options[index];
        option.setAttribute('aria-selected', 'true');
        option.setAttribute('data-active', '');
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
        this.activeOptionIndex = index;
      }

      clearActiveOption() {
        this.getOptions().forEach((option) => {
          option.setAttribute('aria-selected', 'false');
          option.removeAttribute('data-active');
        });
        this.activeOptionIndex = -1;
        this.input?.removeAttribute('aria-activedescendant');
      }

      setExpanded(expanded) {
        this.input?.setAttribute('aria-expanded', String(Boolean(expanded)));
      }

      announce(count) {
        if (!this.statusElement) return;
        this.statusElement.textContent = count == null
          ? ''
          : `${count} ${this.dataset.resultsLabel || ''}`.trim();
      }

      announceError() {
        if (this.statusElement) this.statusElement.textContent = this.dataset.errorLabel || '';
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      connectedCallback() {
        super.connectedCallback();

        this.on(this, 'click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          this.closest('form[is="predictive-search"]')?.close();
        });
      }
    }
  );
}

if (!customElements.get('voice-search')) {
  customElements.define(
    'voice-search',
    class VoiceSearch extends MagnetButton {
      constructor() {
        super();

        this.SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      }

      connectedCallback() {
        super.connectedCallback();

        if (!this.SpeechRecognition) return;

        this.input = this.closest('form')?.querySelector('input[type="search"]');
        if (!this.input) return;

        // Safari: OS voice service is unavailable — keep the icon hidden this session
        if (theme.config.hasSessionStorage && window.sessionStorage.getItem(`${theme.settings.themeName}:voice-unavailable`)) {
          this.setAttribute('hidden', '');
          return;
        }

        this.syncMicrophonePermission();
        this.on(this, 'click', this.onClick.bind(this));

      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.recognition?.abort?.();
      }

      async syncMicrophonePermission() {
        try {
          const status = await navigator.permissions.query({ name: 'microphone' });
          const sync = () => this.toggleAttribute('hidden', status.state === 'denied' || this.unavailable === true);
          sync();
          this.on(status, 'change', sync);
        } catch (e) {
          this.removeAttribute('hidden');
        }
      }

      onClick(event) {
        event.preventDefault();

        if (this.listening) {
          this.recognition?.stop();
          return;
        }

        this.recognition = new this.SpeechRecognition();
        this.recognition.lang = document.documentElement.lang || 'en';
        this.recognition.interimResults = false;
        this.recognition.maxAlternatives = 1;

        this.recognition.addEventListener('result', this.onResult.bind(this));
        this.recognition.addEventListener('end', this.onEnd.bind(this));
        this.recognition.addEventListener('error', this.onEnd.bind(this));

        this.listening = true;
        this.classList.add('listening');
        this.recognition.start();
      }

      onResult(event) {
        const transcript = Array.from(event.results)
          .map((result) => result[0].transcript)
          .join('');

        this.input.value = transcript;
        this.input.focus();
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      onEnd(event) {
        this.listening = false;
        this.classList.remove('listening');

        // Safari: the OS voice service is unavailable
        if (event.error === 'service-not-allowed') this.markUnavailable();
      }

      markUnavailable() {
        this.unavailable = true;
        if (theme.config.hasSessionStorage) {
          window.sessionStorage.setItem(`${theme.settings.themeName}:voice-unavailable`, 'true');
        }

        this.closest('form')?.querySelector('[data-voice-unavailable]')?.removeAttribute('hidden');
        this.setAttribute('hidden', '');
      }
    }, { extends: 'button' }
  );
}
